/**
 * DatajudSection — andamentos do processo via API Pública DataJud (CNJ).
 *
 * Busca sob demanda (quando o modal abre). Mostra classe/grau/vara + assuntos
 * (chips) e a timeline de movimentos (recente→antigo). NÃO traz documentos —
 * só metadados+movimentações (a API nacional do CNJ, que funciona do VPS).
 */
import { useState } from 'react';
import { useDatajud } from '../hooks';
import { fmtDateTime } from '../utils';

function fmtAjuizamento(s: string | null): string {
  if (!s || s.length < 8) return '—';
  // 'AAAAMMDDHHMMSS'
  const a = s.slice(0, 4), m = s.slice(4, 6), d = s.slice(6, 8);
  return `${d}/${m}/${a}`;
}

export function DatajudSection({ idPrazo }: { idPrazo: number }) {
  const { data, isLoading, error } = useDatajud(idPrazo, true);
  const [expandido, setExpandido] = useState(false);

  const movs = data?.movimentos ?? [];
  const mostra = expandido ? movs : movs.slice(0, 6);

  return (
    <div className="bg-slate-50/50 dark:bg-slate-950/40 border border-slate-200 dark:border-slate-800/80 rounded-xl p-4 shadow-sm backdrop-blur-md">
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
        <span>🏛️</span> Andamentos · DataJud{data ? ` (${data.n_movimentos})` : ''}
        <span className="ml-auto text-[9px] font-normal normal-case text-slate-400">CNJ · público</span>
      </h4>

      {isLoading && (
        <p className="text-xs text-slate-400 dark:text-slate-500 italic py-1 flex items-center gap-2">
          <span className="animate-spin">⏳</span> consultando DataJud…
        </p>
      )}

      {error && !isLoading && (
        <p className="text-xs text-slate-400 dark:text-slate-500 italic py-1">
          {String((error as any)?.status) === '404'
            ? '— processo não encontrado no DataJud —'
            : '— DataJud indisponível no momento —'}
        </p>
      )}

      {data && !isLoading && (
        <>
          {/* Cabeçalho: classe / grau / vara + assuntos */}
          <div className="mb-3 space-y-1.5">
            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
              {data.classe && (
                <span className="px-2 py-0.5 rounded bg-blue-500/10 text-blue-700 dark:text-blue-300 font-medium">{data.classe}</span>
              )}
              {data.grau && (
                <span className="px-2 py-0.5 rounded bg-slate-200/70 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-mono">{data.grau}</span>
              )}
              {data.vara && (
                <span className="text-slate-500 dark:text-slate-400">· {data.vara}</span>
              )}
            </div>
            {data.assuntos.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {data.assuntos.map((a, i) => (
                  <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-violet-500/10 text-violet-700 dark:text-violet-300">{a}</span>
                ))}
              </div>
            )}
            <div className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">
              ajuizado {fmtAjuizamento(data.data_ajuizamento)}
              {typeof data.nivel_sigilo === 'number' && data.nivel_sigilo > 0 && ` · sigilo nível ${data.nivel_sigilo}`}
            </div>
          </div>

          {/* Timeline de movimentos */}
          {movs.length > 0 ? (
            <>
              <div className="relative border-l border-slate-200 dark:border-slate-800 ml-1.5 pl-4 space-y-3 py-1">
                {mostra.map((m, i) => (
                  <div key={i} className="relative text-xs">
                    <span className="absolute -left-[20.5px] top-1 h-2 w-2 rounded-full border border-white dark:border-slate-900 bg-cyan-500/70" />
                    <div className="text-[10px] text-slate-400 dark:text-slate-500 font-mono mb-0.5">
                      {m.data ? fmtDateTime(m.data) : '—'}
                    </div>
                    <div className="text-slate-700 dark:text-slate-300">{m.nome ?? '—'}</div>
                  </div>
                ))}
              </div>
              {movs.length > 6 && (
                <button
                  onClick={() => setExpandido((v) => !v)}
                  className="mt-2 text-[11px] text-cyan-600 dark:text-cyan-400 hover:underline"
                >
                  {expandido ? 'ver menos' : `ver todos (${movs.length})`}
                </button>
              )}
            </>
          ) : (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic py-1 text-center">— sem movimentos —</p>
          )}
        </>
      )}
    </div>
  );
}
